/**
 * The ranked screen's panels, as strings.
 *
 * Pure, like ./net-panels and ./progression-panels next to it: markup in,
 * nothing out, no element touched. That is what lets the layout be asserted in
 * Node — a panel that silently renders "NaN" or an empty tier chip is the
 * failure mode here, and it is invisible to a type checker.
 *
 * Every number that means something comes from src/game/rating.ts, which the
 * SERVER also uses to compute them. The tier a player is shown and the tier
 * the ladder sorted them into cannot disagree, because there is one table.
 */

import type { LeaderboardEntry, RankedMatch } from '../game/account';
import {
  PROVISIONAL_MATCHES,
  ratingLabel,
  tierFor,
  tierProgress,
  winRate,
  type RatingRecord,
} from '../game/rating';
import { isMultiplayerMode, modeSpec } from '../net/modes';

/** Escape the four characters that can break out of a node or an attribute. */
const esc = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * A mode's display name, falling back to the raw string.
 *
 * The fallback is not defensive padding: history rows carry whatever mode
 * string the server wrote when the match was played, and a build that has
 * since renamed or dropped a mode must still be able to render last month's
 * matches rather than showing a blank column.
 */
const modeName = (mode: string): string => (isMultiplayerMode(mode) ? modeSpec(mode).label : mode);

/**
 * The player's own standing in one mode.
 *
 * A provisional player is shown their PROGRESS towards being rated rather than
 * a tier, because the tier does not mean anything yet — see `ratingLabel`. The
 * bar underneath is progress through the current band, and it is deliberately
 * the same bar in both cases: a screen whose main element changes shape halfway
 * through a player's first evening reads as two different screens.
 */
export const standingMarkup = (record: RatingRecord | null): string => {
  if (record === null) {
    return (
      `<div class="rw-rank-card rw-rank-empty">` +
      `<div class="rw-rank-label">Unrated</div>` +
      `<div class="rw-rank-note">Play a ranked match — one with people on both sides — to join the ladder.</div>` +
      `</div>`
    );
  }

  const tier = tierFor(record.rating);
  const provisional = record.played < PROVISIONAL_MATCHES;
  const fill = provisional ? record.played / PROVISIONAL_MATCHES : tierProgress(record.rating);
  return (
    `<div class="rw-rank-card" style="--rw-tier:${esc(tier.color)}">` +
    `<div class="rw-rank-head">` +
    `<div class="rw-rank-mode">${esc(modeName(record.mode))}</div>` +
    `<div class="rw-rank-rating">${Math.round(record.rating)}</div>` +
    `</div>` +
    `<div class="rw-rank-label">${esc(ratingLabel(record.rating, record.played))}</div>` +
    `<div class="rw-rank-bar"><i style="transform:scaleX(${fill.toFixed(3)})"></i></div>` +
    `<div class="rw-rank-record">` +
    `<span>${record.wins}W</span><span>${record.losses}L</span><span>${record.draws}D</span>` +
    `<span class="rw-rank-rate">${winRate(record)}%</span>` +
    `</div>` +
    `</div>`
  );
};

/** Every mode the player has a standing in, best first. */
export const standingsMarkup = (records: readonly RatingRecord[]): string => {
  if (records.length === 0) return standingMarkup(null);
  return `<div class="rw-rank-cards">${records.map(r => standingMarkup(r)).join('')}</div>`;
};

/**
 * The ladder for one mode.
 *
 * `you` highlights the signed-in player's row. Passing their id rather than
 * their name because two accounts may not share a display name today but the
 * uniqueness of one is a database constraint, not a truth — and a highlight on
 * the wrong row is worse than none.
 */
export const leaderboardMarkup = (
  entries: readonly LeaderboardEntry[],
  you: number | null
): string => {
  if (entries.length === 0) {
    return (
      `<div class="rw-rank-empty">Nobody has finished ${PROVISIONAL_MATCHES} ranked matches in this ` +
      `mode yet. The board fills up as people play.</div>`
    );
  }
  const rows = entries
    .map(entry => {
      const tier = tierFor(entry.rating);
      const mine = you !== null && entry.userId === you ? ' mine' : '';
      return (
        `<div class="rw-board-row${mine}">` +
        `<span class="rw-board-rank">${entry.rank}</span>` +
        `<span class="rw-board-dot" style="background:${esc(tier.color)}"></span>` +
        `<span class="rw-board-name">${esc(entry.displayName)}</span>` +
        `<span class="rw-board-played">${entry.played}</span>` +
        `<span class="rw-board-rating">${Math.round(entry.rating)}</span>` +
        `</div>`
      );
    })
    .join('');
  return (
    `<div class="rw-board">` +
    `<div class="rw-board-head">` +
    `<span class="rw-board-rank">#</span><span class="rw-board-dot"></span>` +
    `<span class="rw-board-name">Player</span>` +
    `<span class="rw-board-played">Played</span>` +
    `<span class="rw-board-rating">Rating</span>` +
    `</div>${rows}</div>`
  );
};

/** A rating movement, signed, or a dash for a match that did not count. */
export const changeLabel = (match: RankedMatch): string => {
  if (!match.ranked) return '—';
  const delta = match.ratingAfter - match.ratingBefore;
  if (delta === 0) return '0';
  return delta > 0 ? `+${delta}` : String(delta);
};

/**
 * The player's recent matches, newest first.
 *
 * An unranked match is shown, greyed, rather than hidden. "Where did my last
 * game go" is a worse question to leave a player with than "why did that one
 * not count", and the row answers the second one on its face.
 */
export const historyMarkup = (matches: readonly RankedMatch[]): string => {
  if (matches.length === 0) {
    return `<div class="rw-rank-empty">No multiplayer matches yet.</div>`;
  }
  const rows = matches
    .map(match => {
      const change = changeLabel(match);
      const tone = !match.ranked ? 'unranked' : match.outcome;
      return (
        `<div class="rw-hist-row ${tone}">` +
        `<span class="rw-hist-outcome">${match.outcome === 'win' ? 'Win' : match.outcome === 'loss' ? 'Loss' : 'Draw'}</span>` +
        `<span class="rw-hist-mode">${esc(modeName(match.mode))}</span>` +
        `<span class="rw-hist-score">${match.scoreFor}&ndash;${match.scoreAgainst}</span>` +
        `<span class="rw-hist-goals">${match.goals} G</span>` +
        `<span class="rw-hist-change">${esc(change)}</span>` +
        `</div>`
      );
    })
    .join('');
  return `<div class="rw-hist">${rows}</div>`;
};

/** The whole panel: standing, then the board, then the history. */
export const rankedPanelMarkup = (view: {
  ratings: readonly RatingRecord[];
  entries: readonly LeaderboardEntry[];
  matches: readonly RankedMatch[];
  mode: string;
  userId: number | null;
  loading: boolean;
  error: string | null;
}): string => {
  if (view.error !== null) return `<div class="rw-rank-empty">${esc(view.error)}</div>`;
  if (view.loading && view.ratings.length === 0 && view.entries.length === 0) {
    return `<div class="rw-rank-empty">Loading…</div>`;
  }
  return (
    standingsMarkup(view.ratings) +
    `<div class="rw-rank-title">${esc(modeName(view.mode))} Leaderboard</div>` +
    leaderboardMarkup(view.entries, view.userId) +
    `<div class="rw-rank-title">Recent Matches</div>` +
    historyMarkup(view.matches)
  );
};
